import { Clear } from '@mui/icons-material';
import { Box, Button, Container, IconButton, Stack, styled, Typography, useTheme } from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
// import ImageComponent from 'src/components/common/Image';
import ImageComponent from 'src/components/common/Image';
import RouterLinkNoUnderline from 'src/components/common/RouterLinkNoUnderline';
import Drawer from 'src/components/Drawer';
import { PageErrorBoundary } from 'src/components/ErrorBoundary/PageErrorBoundary';
import Footer from 'src/components/Footer';
import useLayout from 'src/hooks/useLayout';
import IframeLayout from 'src/layouts/IframeLayout';
import {
  CLOSE_BANNER_BOOKING_CALENDAR,
  DRAWER_WIDTH,
  DRAWER_WIDTH_CLOSE,
  HEADER_HEIGHT,
} from 'src/utils/constants/config';
import { BANNER_BOOKING_DEMO_CALENDAR, ICON_BANNER_HOME_PAGE } from 'src/utils/constants/imagesUrl';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { useMenuStore } from 'src/zustand/useMenuStore';

const ContainerMainLayout = styled(Box)(() => ({
  minHeight: '100vh',
  // minWidth: '1200px',
  overflowX: 'scroll',
}));

const Banner = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  background: ' linear-gradient(91deg, #422AFB 2.03%, #5451F8 98.7%)',
  borderRadius: theme.shape.borderRadius * 2,
  zIndex: 1000,
  position: 'relative',
  padding: theme.spacing(2),
  gap: theme.spacing(4),
}));

const MainLayout = ({ children, hideSideBar }: { children: ReactNode; hideSideBar?: boolean }) => {
  const localHiddenBanner = window?.localStorage?.getItem(CLOSE_BANNER_BOOKING_CALENDAR);
  const theme = useTheme();
  const [hiddenBanner, setHiddenBanner] = useState(Boolean(localHiddenBanner));
  const { isOpen, dispatch } = useMenuStore();
  const location = useLocation();
  const { isEmbedded } = useLayout();
  const currentTitle = document?.title;

  const closeBannerBookingCalendar = () => {
    window?.localStorage?.setItem(CLOSE_BANNER_BOOKING_CALENDAR, 'true');
    setHiddenBanner(true);
  };

  const isShowBannerBooking = useMemo(() => {
    return (
      location.pathname !== AuthRouteKeys.CalendarBooking && (!hiddenBanner || Boolean(localHiddenBanner) === false)
    );
  }, [localHiddenBanner]);

  if (isEmbedded) {
    return <IframeLayout>{children}</IframeLayout>;
  }

  return (
    <ContainerMainLayout sx={{ background: theme.palette.background.paper1 }}>
      <Drawer open={isOpen} handleOpen={(value) => dispatch(value)} hideSideBar={hideSideBar} />
      <Box
        id="adamftd-body-global"
        sx={{
          marginLeft: isOpen ? DRAWER_WIDTH + 'px' : DRAWER_WIDTH_CLOSE + 'px',
          mt: HEADER_HEIGHT + 'px',
          minHeight: `calc(100vh - ${theme.spacing(20)})`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          width: isOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : `calc(100% - ${DRAWER_WIDTH_CLOSE}px)`,
        }}
      >
        {isShowBannerBooking && (
          <Container maxWidth="xl" sx={{ paddingTop: theme.spacing(2) }}>
            <Banner>
              <ImageComponent
                imgSrc={BANNER_BOOKING_DEMO_CALENDAR}
                style={{ objectFit: 'cover', display: 'inline-block' }}
                height={120}
              />
              <ImageComponent
                imgSrc={ICON_BANNER_HOME_PAGE}
                style={{ position: 'absolute', top: 0, right: 0 }}
                height="100%"
              />
              <Stack justifyContent="center" flex={4} gap={1}>
                <Typography variant="h5" color="white" align="left">
                  Book Your 30-Minute Tutorial Session Now
                </Typography>
                <Typography variant="body1" color="white" align="left">
                  Get the most out of ADAMftd from day one. Schedule a quick 30-minute session below for guided
                  tutorials on all our platform's powerful features.
                </Typography>
              </Stack>
              <Stack justifyContent="center" flex={1}>
                <RouterLinkNoUnderline to={AuthRouteKeys.CalendarBooking}>
                  <Button variant="outlined" color="inheritWhite">
                    Book Now
                  </Button>
                </RouterLinkNoUnderline>
              </Stack>
              <IconButton
                sx={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                }}
                size="small"
                onClick={() => closeBannerBookingCalendar()}
              >
                <Clear fontSize="small" sx={{ color: theme.palette.common.white }} />
              </IconButton>
            </Banner>
          </Container>
        )}
        <PageErrorBoundary pageName={currentTitle}>{children}</PageErrorBoundary>
      </Box>
      <Box
        sx={{
          marginLeft: isOpen ? DRAWER_WIDTH + 'px' : DRAWER_WIDTH_CLOSE + 'px',
        }}
      >
        <Container maxWidth="xl">
          <Footer />
        </Container>
      </Box>
    </ContainerMainLayout>
  );
};

export default MainLayout;
